import React, { useMemo, useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ConsequenceReveal from '../shared/ConsequenceReveal';
import styles from './MemoryMatchEngine.module.css';

const MemoryMatchEngine = ({ gamifiedQuestion, onAnswer, accent = '#264653' }) => {
  const [cards, setCards] = useState([]);
  const [flipped, setFlipped] = useState([]);
  const [matched, setMatched] = useState([]);
  const [selected, setSelected] = useState(null);
  const [showOutcome, setShowOutcome] = useState(false);
  const [gameComplete, setGameComplete] = useState(false);
  const [isLocked, setIsLocked] = useState(false);

  const options = useMemo(() => gamifiedQuestion?.options?.slice(0, 4) || [], [gamifiedQuestion?.options]);
  const selectedOption = options.find((option) => option.label === selected);

  // Initialize cards from scene data or generate from options
  useEffect(() => {
    const sceneElements = gamifiedQuestion?.scene?.elements || {};
    const pairs = sceneElements.pairs || [
      { id: 1, concept: 'Formative', definition: 'Monitor learning during instruction' },
      { id: 2, concept: 'Summative', definition: 'Evaluate after instruction' },
      { id: 3, concept: 'Diagnostic', definition: 'Identify prior knowledge gaps' },
      { id: 4, concept: 'Ipsative', definition: 'Compare to own past performance' }
    ];

    // Create card deck: concepts on left, definitions on right
    const deck = [
      ...pairs.map((pair) => ({
        id: `concept-${pair.id}`,
        type: 'concept',
        content: pair.concept,
        matchId: pair.id,
        label: options[pair.id - 1]?.label || String.fromCharCode(64 + pair.id)
      })),
      ...pairs.map((pair) => ({
        id: `definition-${pair.id}`,
        type: 'definition',
        content: pair.definition,
        matchId: pair.id,
        label: null // Definitions don't map directly to options
      }))
    ];

    // Shuffle definitions (keep concepts in position)
    const shuffled = [...deck].sort(() => Math.random() - 0.5);
    setCards(shuffled);
    setFlipped([]);
    setMatched([]);
    setSelected(null);
    setShowOutcome(false);
    setGameComplete(false);
  }, [gamifiedQuestion, options]);

  const handleCardClick = (card) => {
    if (isLocked || flipped.includes(card.id) || matched.includes(card.matchId)) return;

    const newFlipped = [...flipped, card.id];
    setFlipped(newFlipped);

    if (newFlipped.length === 2) {
      setIsLocked(true);

      const [firstId, secondId] = newFlipped;
      const firstCard = cards.find(c => c.id === firstId);
      const secondCard = cards.find(c => c.id === secondId);

      if (firstCard.matchId === secondCard.matchId) {
        // Match found!
        setTimeout(() => {
          setMatched(prev => [...prev, firstCard.matchId]);
          setFlipped([]);
          setIsLocked(false);

          // If first match, set answer to the concept's option
          if (matched.length === 0) {
            const optionLabel = firstCard.label || secondCard.label;
            if (optionLabel) {
              setSelected(optionLabel);
            }
          }

          // Check if all matched
          if (matched.length + 1 === 4) {
            setGameComplete(true);
            setTimeout(() => setShowOutcome(true), 800);
          }
        }, 600);
      } else {
        // No match - flip back after delay
        setTimeout(() => {
          setFlipped([]);
          setIsLocked(false);
        }, 800);
      }
    }
  };

  const handleSkip = () => {
    // If game isn't complete, register current selection or first option
    if (!selected && options.length > 0) {
      setSelected(options[0].label);
    }
    setShowOutcome(true);
  };

  return (
    <div className={styles.engine} style={{ '--accent': accent }}>
      {/* SCENE AREA - Top */}
      <div className={styles.sceneArea}>
        <motion.div
          className={styles.cardGrid}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          {/* Cards arranged in 2x4 grid */}
          <div className={styles.gridContainer}>
            {cards.map((card, index) => {
              const isFlipped = flipped.includes(card.id);
              const isMatched = matched.includes(card.matchId);
              const row = Math.floor(index / 4);
              const col = index % 4;

              return (
                <motion.div
                  key={card.id}
                  className={`${styles.cardWrapper} ${isMatched ? styles.matched : ''}`}
                  initial={{ opacity: 0, y: -20, rotateY: 0 }}
                  animate={{
                    opacity: 1,
                    y: 0,
                    rotateY: isFlipped || isMatched ? 180 : 0
                  }}
                  transition={{
                    opacity: { delay: index * 0.08 },
                    y: { delay: index * 0.08, type: 'spring', stiffness: 100 },
                    rotateY: { duration: 0.4 }
                  }}
                  style={{ perspective: 1000 }}
                  onClick={() => handleCardClick(card)}
                >
                  <div className={`${styles.card} ${isFlipped || isMatched ? styles.flipped : ''}`}>
                    {/* Front face (card back) */}
                    <div className={styles.cardFront}>
                      <svg viewBox="0 0 60 40" className={styles.cardPattern}>
                        <defs>
                          <pattern id={`pattern-${index}`} width="10" height="10" patternUnits="userSpaceOnUse">
                            <line x1="0" y1="5" x2="10" y2="5" stroke="#e0e0e0" strokeWidth="1" />
                            <line x1="5" y1="0" x2="5" y2="10" stroke="#e0e0e0" strokeWidth="1" />
                          </pattern>
                        </defs>
                        <rect width="60" height="40" fill={`url(#pattern-${index})`} />
                        <rect width="60" height="40" fill="none" stroke="#1a1a1a" strokeWidth="2" rx="4" />
                      </svg>
                    </div>

                    {/* Back face (card content) */}
                    <div className={styles.cardBack}>
                      <span className={styles.cardContent}>{card.content}</span>
                      {card.type === 'concept' && (
                        <span className={styles.cardType}>Concept</span>
                      )}
                    </div>

                    {/* Match glow effect */}
                    {isMatched && (
                      <motion.div
                        className={styles.matchGlow}
                        initial={{ scale: 0, opacity: 0 }}
                        animate={{ scale: [1, 1.5, 1], opacity: [0.6, 0, 0.6] }}
                        transition={{ repeat: 2, duration: 0.8 }}
                      />
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Connection line for matched pairs */}
          <AnimatePresence>
            {matched.map((matchId, index) => (
              <motion.div
                key={matchId}
                className={styles.matchLine}
                initial={{ pathLength: 0, opacity: 0 }}
                animate={{ pathLength: 1, opacity: 1 }}
                transition={{ delay: index * 0.2, duration: 0.5 }}
              />
            ))}
          </AnimatePresence>
        </motion.div>

        {/* Match counter */}
        <div className={styles.matchCounter}>
          <span className={styles.counterLabel}>Matches:</span>
          <span className={styles.counterValue}>{matched.length} / 4</span>
        </div>
      </div>

      {/* QUESTION TEXT */}
      <div className={styles.questionText}>
        {gamifiedQuestion?.situation}
      </div>

      {/* ANSWER OPTIONS - Bottom */}
      <div className={styles.optionsArea}>
        {options.map((option, index) => (
          <motion.div
            key={option.label}
            className={`${styles.optionCard} ${selected === option.label ? styles.selected : ''}`}
            initial={{ y: 30, opacity: 0 }}
            animate={{
              y: 0,
              opacity: selected && selected !== option.label ? 0.3 : 1,
              scale: matched.includes(index + 1) ? 1.02 : 1
            }}
            transition={{ delay: 0.6 + index * 0.1 }}
          >
            <span className={styles.optionLabel}>{option.label}</span>
            <span className={styles.optionText}>{option.text}</span>
            {matched.includes(index + 1) && (
              <motion.span
                className={styles.matchBadge}
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', stiffness: 200 }}
              >
                ✓
              </motion.span>
            )}
          </motion.div>
        ))}
      </div>

      {/* Skip button for incomplete games */}
      {!gameComplete && (
        <motion.button
          className={styles.skipBtn}
          onClick={handleSkip}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2 }}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          Skip to Results
        </motion.button>
      )}

      {/* Narration */}
      <p className={styles.narration}>{gamifiedQuestion?.narration}</p>

      <ConsequenceReveal
        visible={showOutcome}
        tone={selected === 'A' ? 'success' : 'warning'}
        message={selectedOption?.consequence || 'Memory match complete.'}
        duration={1700}
        onDone={() => {
          setShowOutcome(false);
          if (selected) onAnswer(selected);
        }}
      />
    </div>
  );
};

export default MemoryMatchEngine;
